import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, Search } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { AgentControls, PriorityChips } from '../components/todos/TaskFilters';
import { StatsCards } from '../components/todos/StatsCards';
import { TodosResults } from '../components/todos/TodosResults';
import { useFollowups } from '../components/todos/useFollowups';

function PageHeader({ subtitle }: { subtitle: string }) {
  return (
    <div className="pt-4">
      <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
        <ClipboardList className="text-purple-600" size={28} />
        Tasks
      </h1>
      <p className="text-sm text-gray-500 mt-1">
        Follow-up tasks from visits · {subtitle}
      </p>
    </div>
  );
}

function PosSearch({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="relative">
      <Search
        size={18}
        className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
      />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search PoS by name, address or city..."
        className={
          'w-full pl-11 pr-4 py-3 rounded-xl border-2 border-gray-200 ' +
          'focus:border-purple-500 focus:outline-none text-sm'
        }
      />
    </div>
  );
}

const Todos: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const s = useFollowups(user?.id);

  // The agent list only holds agents with pending tasks, so fall back to the own name.
  const agentName =
    s.selectedAgentName ??
    (s.agentFilter && s.agentFilter === user?.id ? user.fullName : null);

  const subtitle = agentName
    ? `${s.visibleFollowups.length} tasks · ${agentName}`
    : `${s.visibleFollowups.length} tasks`;

  return (
    <div className="max-w-6xl mx-auto space-y-4 px-4 pb-6">
      <PageHeader subtitle={subtitle} />
      {s.stats && <StatsCards stats={s.stats} />}
      <PriorityChips
        filter={s.filter}
        stats={s.stats}
        myUserId={user?.id}
        agentFilter={s.agentFilter}
        onFilterChange={s.setFilter}
        onMyTasks={() => user && s.setAgentFilter(user.id)}
      />
      <AgentControls
        agents={s.agents}
        agentFilter={s.agentFilter}
        groupByAgentOn={s.groupByAgentOn}
        onAgentChange={s.setAgentFilter}
        onToggleGroup={() => s.setGroupByAgentOn((v) => !v)}
      />
      <PosSearch value={s.search} onChange={s.setSearch} />
      <TodosResults
        loading={s.loading}
        visible={s.visibleFollowups}
        totalLoaded={s.followups.length}
        searchActive={!!s.search.trim()}
        agentName={agentName}
        groups={s.agentGroups}
        onOpenPos={(posId) => navigate(`/pos/${posId}`)}
        onFullVisit={(f) => {
          navigate(
            `/visits/new?posId=${encodeURIComponent(f.pos_id)}&resolveFollowupId=${encodeURIComponent(f.id)}`
          );
        }}
        onRefresh={s.fetchData}
      />
    </div>
  );
};

export default Todos;
